import { inRange } from '../../../core/utils/general/general';
import { Direction } from '../../../windows/helpers/adjustWindow';
import {
  CellPosition,
  Col,
  Row,
  Sheet,
  SheetView,
  SheetViewportView,
  Viewport,
} from '../../types';
import {
  getFreezeBarHandleSize,
  getViewportEnd,
  getViewportHeight,
  getViewportStart,
  getViewportWidth,
} from '../viewport/viewportUtils';
import { createViewportForward, getRowHeaderWidth } from '../sheet/uiData';
import { getViewportStartEnd } from './virtualViewport';
import { SheetDefaults } from '../../constants/sheetDefaults';
import { DataDirection } from '../../../core/types/ui/position';
import { HeaderGroup } from '../../types/sheet/headerGroups';

export const calculateGroupBarSize = (
  groups: HeaderGroup[],
  defaultSize: number
) => {
  return getGroupsDepth(groups) * defaultSize;
};

const getGroupsDepth = (groups: HeaderGroup[], depth = 0) => {
  if (!groups.length) {
    return 0;
  }

  let nestedDepth = depth + 1;

  for (const group of groups) {
    const groupDepth = depth + 1;
    if (group.nestedGroups.length) {
      const newDepth = getGroupsDepth(group.nestedGroups, groupDepth);
      if (newDepth > nestedDepth) nestedDepth = newDepth;
    }
  }

  return nestedDepth;
};

interface ViewportConfigs {
  start?: number;
  end?: number;
  fitToCenter?: boolean;
}
export const getViewportRows = (
  windowWidth: number,
  windowHeight: number,
  sheet: Sheet,
  sheetViewportView: SheetViewportView,
  direction: Direction.Top | Direction.Bottom,
  configs: ViewportConfigs = {}
  // start?: number,
  // end?: number
): {
  colViewport?: Viewport;
  rowViewport: Viewport;
} => {
  let { start, end, fitToCenter } = configs;
  const { filterResults, rowViewport } = sheetViewportView;
  const { freezedRows } = sheet;
  const frozenEnd = freezedRows.frozen ? freezedRows.end + 1 : 0;
  let frozenIndexes: number[] = [];
  let frozenSizes: number[] = [];
  let frozenSum = 0;
  let frozenSize = 0;

  const colGroupBarHeight = calculateGroupBarSize(
    sheet.colGroups,
    SheetDefaults.groupBarHeight
  );

  const viewportHeight = getViewportHeight(
    windowHeight,
    sheet,
    colGroupBarHeight
  );
  if (freezedRows.frozen) {
    const frozenData = createViewportForward(
      freezedRows.start,
      freezedRows.end,
      sheet.rowSizes,
      sheet.defaultRowHeight,
      sheet.hiddenRows,
      sheet.zoom,
      freezedRows,
      filterResults,
      viewportHeight
    );
    frozenSum = frozenData.sum;
    frozenSize = frozenSum + getFreezeBarHandleSize(freezedRows, sheet.zoom);
    frozenIndexes = frozenData.indexes;
    frozenSizes = frozenData.sizes;
  }

  const availableHeight = viewportHeight - frozenSize;

  // In case of fit center set the index at the first half end
  // And then calculate the expected start index
  if (fitToCenter && start !== undefined) {
    const halfHeight = Math.round(availableHeight / 2);
    if (
      !freezedRows.frozen ||
      !inRange(start, freezedRows.start, freezedRows.end)
    ) {
      const { indexes } = getViewportStart(
        sheet.rowSizes,
        sheet.hiddenRows,
        halfHeight,
        start,
        frozenEnd,
        sheet.maxRowIndex,
        sheet.defaultRowHeight,
        sheet.zoom,
        sheet.freezedRows,
        filterResults,
        frozenIndexes
      );
      const range = getViewportStartEnd(indexes);
      start = range.start;
    }
  }

  const { start: oldRs, end: oldRe } = getViewportStartEnd(
    rowViewport.nonFrozenIndexes
  );
  let rs = start ?? oldRs;
  let re = end ?? oldRe; //Math.max(oldRe, frozenEnd);

  let nonFrozenIndexes: number[] = [];
  let nonFrozenSizes: number[] = [];
  let nonFrozenSum: number = 0;

  if (availableHeight > 0) {
    if (direction === Direction.Bottom) {
      const { indexes, sizes, sum } = getViewportEnd(
        sheet.rowSizes,
        sheet.hiddenRows,
        availableHeight,
        rs,
        frozenEnd, // Min
        sheet.maxRowIndex, // Max
        sheet.defaultRowHeight,
        sheet.zoom,
        sheet.freezedRows,
        filterResults,
        frozenIndexes
      );
      nonFrozenIndexes = indexes;
      nonFrozenSum = sum;
      nonFrozenSizes = sizes;
    } else {
      const { indexes, sum, sizes } = getViewportStart(
        sheet.rowSizes,
        sheet.hiddenRows,
        availableHeight,
        re,
        frozenEnd,
        sheet.maxRowIndex,
        sheet.defaultRowHeight,
        sheet.zoom,
        sheet.freezedRows,
        filterResults,
        frozenIndexes
      );
      nonFrozenIndexes = indexes;
      nonFrozenSum = sum;
      nonFrozenSizes = sizes;
    }
  }

  //   If row header width changes then calcualte col viewport
  const oldRowHeaderWidth = getRowHeaderWidth(re, sheet.zoom);
  const { end: newRe } = getViewportStartEnd(nonFrozenIndexes);

  const newRowHeaderWidth = getRowHeaderWidth(newRe, sheet.zoom);

  const newRowViewport: Viewport = {
    frozenIndexes,
    frozenSizes,
    frozenSum,
    nonFrozenIndexes,
    nonFrozenSizes,
    nonFrozenSum,
    alternateEntityGroupBarSize: colGroupBarHeight,
    // groupBarSize: rowGroupBarWidth,
  };

  let colViewport: Viewport | undefined;
  if (oldRowHeaderWidth !== newRowHeaderWidth) {
    colViewport = getViewportCols(
      windowWidth,
      sheet,
      {
        ...sheetViewportView,
        rowViewport: newRowViewport,
      },
      Direction.Right
    );
  }

  return {
    rowViewport: newRowViewport,
    colViewport: colViewport,
  };
};

export const getViewportCols = (
  windowWidth: number,
  sheet: Sheet,
  { colViewport, rowViewport }: SheetViewportView,
  direction: Direction.Left | Direction.Right,
  configs: ViewportConfigs = {}
  // start?: number,
  // end?: number
): Viewport => {
  let { start, end, fitToCenter } = configs;

  const { freezedCols } = sheet;
  const frozenEnd = freezedCols.frozen ? freezedCols.end + 1 : 0;
  let frozenSum = 0;
  let frozenIndexes: number[] = [];
  let frozenSizes: number[] = [];
  let frozenSize = 0;

  const rowGroupBarWidth = calculateGroupBarSize(
    sheet.rowGroups,
    SheetDefaults.rowGroupBarWidth
  );
  // const colGroupBarSize = calculateGroupBarSize(
  //   sheet.colGroups,
  //   SheetDefaults.groupBarHeight
  // );

  const { end: re } = getViewportStartEnd(rowViewport.nonFrozenIndexes);
  const viewportWidth = getViewportWidth(
    windowWidth,
    sheet,
    re,
    rowGroupBarWidth
  );
  if (freezedCols.frozen) {
    const frozenData = createViewportForward(
      freezedCols.start,
      freezedCols.end,
      sheet.colSizes,
      sheet.defaultColWidth,
      sheet.hiddenCols,
      sheet.zoom,
      freezedCols,
      undefined,
      viewportWidth
    );
    frozenSum = frozenData.sum;
    frozenSize = frozenSum + getFreezeBarHandleSize(freezedCols, sheet.zoom);
    frozenIndexes = frozenData.indexes;
    frozenSizes = frozenData.sizes;
  }

  const availableWidth = viewportWidth - frozenSize;

  if (fitToCenter && start !== undefined) {
    const halfWidth = Math.round(availableWidth / 2);
    if (
      !freezedCols.frozen ||
      !inRange(start, freezedCols.start, freezedCols.end)
    ) {
      const { indexes } = getViewportStart(
        sheet.colSizes,
        sheet.hiddenCols,
        halfWidth,
        start,
        frozenEnd,
        sheet.maxColIndex,
        sheet.defaultColWidth,
        sheet.zoom,
        sheet.freezedCols
      );
      const range = getViewportStartEnd(indexes);
      start = range.start;
    }
  }

  const { start: oldCs, end: oldCe } = getViewportStartEnd(
    colViewport.nonFrozenIndexes
  );
  let cs = start ?? oldCs;
  let ce = end ?? oldCe;

  let nonFrozenIndexes: number[] = [];
  let nonFrozenSizes: number[] = [];
  let nonFrozenSum: number = 0;

  if (availableWidth > 0) {
    if (direction === Direction.Right) {
      const { indexes, sum, sizes } = getViewportEnd(
        sheet.colSizes,
        sheet.hiddenCols,
        availableWidth,
        cs, //Start
        frozenEnd, // Min
        sheet.maxColIndex, // Max
        sheet.defaultColWidth,
        sheet.zoom,
        sheet.freezedCols
      );
      nonFrozenIndexes = indexes;
      nonFrozenSum = sum;
      nonFrozenSizes = sizes;
    } else {
      const { indexes, sizes, sum } = getViewportStart(
        sheet.colSizes,
        sheet.hiddenCols,
        availableWidth,
        ce,
        frozenEnd,
        sheet.maxColIndex,
        sheet.defaultColWidth,
        sheet.zoom,
        sheet.freezedCols
      );
      nonFrozenIndexes = indexes;
      nonFrozenSum = sum;
      nonFrozenSizes = sizes;
    }
  }

  return {
    frozenIndexes,
    frozenSizes,
    frozenSum,
    nonFrozenIndexes,
    nonFrozenSizes,
    nonFrozenSum,
    alternateEntityGroupBarSize: rowGroupBarWidth,
  };
};

interface GetViewportConfigs {
  cellPosition?: Partial<CellPosition>;
  fitToCenter?: boolean;
}
export const getViewport = (
  windowWidth: number,
  windowHeight: number,
  sheet: Sheet,
  sheetViewportView: SheetViewportView,
  colDirection: Direction.Left | Direction.Right,
  rowDirection: Direction.Top | Direction.Bottom,
  configs: GetViewportConfigs = {}
): Pick<SheetView, 'colViewport' | 'rowViewport'> => {
  const colViewport = getViewportCols(
    windowWidth,
    sheet,
    sheetViewportView,
    colDirection,
    {
      start: configs.cellPosition?.ci,
      fitToCenter: configs.fitToCenter,
    }
  );

  const rowData = getViewportRows(
    windowWidth,
    windowHeight,
    sheet,
    {
      ...sheetViewportView,
      colViewport,
    },
    rowDirection,
    {
      start: configs.cellPosition?.ri,
      fitToCenter: configs.fitToCenter,
    }
  );

  return {
    colViewport: rowData.colViewport ?? colViewport,
    rowViewport: rowData.rowViewport,
  };
};

interface FitCellInViewportConfigs {
  ignoreRow?: boolean;
  ignoreCol?: boolean;
}
export const fitCellInViewport = (
  sheet: Sheet,
  cellPosition: CellPosition,
  sheetViewportView: SheetViewportView,
  windowWidth: number,
  windowHeight: number,
  configs?: FitCellInViewportConfigs
): Partial<Pick<SheetView, 'colViewport' | 'rowViewport'>> => {
  let colViewport = undefined;
  let rowViewport = undefined;
  if (!configs?.ignoreCol) {
    colViewport = fitColInViewport(
      cellPosition.ci,
      sheetViewportView,
      sheet,
      windowWidth
    );
  }
  if (!configs?.ignoreRow) {
    const rowData = fitRowInViewport(
      cellPosition.ri,
      {
        ...sheetViewportView,
        colViewport: colViewport ?? sheetViewportView.colViewport,
      },
      sheet,
      windowWidth,
      windowHeight
    );
    if (rowData.colViewport) colViewport = rowData.colViewport;
    rowViewport = rowData.rowViewport;
  }

  return {
    rowViewport,
    colViewport,
  };
};

export const fitRowInViewport = (
  ri: number,
  sheetViewportView: SheetViewportView,
  sheet: Sheet,
  windowWidth: number,
  windowHeight: number
): Partial<Pick<SheetView, 'colViewport' | 'rowViewport'>> => {
  const { frozen, start, end } = sheet.freezedRows;
  const { rowViewport } = sheetViewportView;
  if (frozen && inRange(ri, start, end)) return {};

  const { start: rs, end: re } = getViewportStartEnd(
    rowViewport.nonFrozenIndexes
  );

  let direction: Direction | undefined = undefined;

  if (ri < rs) {
    direction = Direction.Bottom;
  } else if (ri >= re) {
    direction = Direction.Top;
  }

  if (direction)
    return getViewportRows(
      windowWidth,
      windowHeight,
      sheet,
      sheetViewportView,
      direction,
      {
        start: direction === Direction.Bottom ? ri : undefined,
        end: direction === Direction.Top ? ri : undefined,
      }
    );

  return {};
};

export const fitColInViewport = (
  ci: number,
  sheetViewportView: SheetViewportView,
  sheet: Sheet,
  windowWidth: number
): Viewport | undefined => {
  const { colViewport } = sheetViewportView;
  const { frozen, start, end } = sheet.freezedCols;
  if (frozen && inRange(ci, start, end)) return;

  const { start: cs, end: ce } = getViewportStartEnd(
    colViewport.nonFrozenIndexes
  );

  let direction: Direction | undefined = undefined;

  if (ci < cs) {
    direction = Direction.Right;
  } else if (ci >= ce) {
    direction = Direction.Left;
  }

  if (direction)
    return getViewportCols(windowWidth, sheet, sheetViewportView, direction, {
      start: direction === Direction.Right ? ci : undefined,
      end: direction === Direction.Left ? ci : undefined,
    });

  return;
};

// export const fitRowInViewportCenter = (
//   ri: number,
//   sheetViewportView: SheetView,
//   sheet: Sheet,
//   windowWidth: number,
//   windowHeight: number
// ) => {
//   const { rowViewport, colViewport } = sheetViewportView;
//   const { freezedRows } = sheet;
//   let frozenEnd = 0;
//   if (freezedRows.frozen) {
//     if (inRange(ri, freezedRows.start, freezedRows.end)) return;
//     frozenEnd = freezedRows.end + 1;
//   }

//   const viewportHeight = getViewportHeight(
//     windowHeight,
//     sheet,
//     rowViewport.alternateEntityGroupBarSize
//   );

//   const halfHeight = Math.round(viewportHeight / 2);

//   const { indexes, sum } = getViewportEnd(
//     sheet.rows,
//     sheet.hiddenRows,
//     availableHeight,
//     rs,
//     frozenEnd, // Min
//     sheet.maxRowIndex, // Max
//     sheet.defaultRowHeight,
//     sheet.zoom,
//     sheet.freezedRows,
//     filterResults,
//     frozenIndexes
//   );
// };

// export const fitColInViewportCenter = (
//   ci: number,
//   sheetViewportView: SheetView,
//   sheet: Sheet,
//   windowWidth: number
// ) => {
//   const { rowViewport, colViewport } = sheetViewportView;
//   const { freezedCols } = sheet;
//   let frozenEnd = 0;
//   if (freezedCols.frozen) {
//     if (inRange(ci, freezedCols.start, freezedCols.end)) return;

//     frozenEnd = freezedCols.end + 1;
//   }
//   // const frozenEnd = sheet.freezedCols.frozen ? sheet.freezedCols.end + 1 : 0;

//   const { end: re } = getViewportStartEnd(rowViewport.nonFrozenIndexes);
//   const viewportWidth = getViewportWidth(
//     windowWidth,
//     sheet,
//     re,
//     colViewport.alternateEntityGroupBarSize // rowGroupbar Width
//   );
//   const availableWidth = viewportWidth - colViewport.frozenSum;
//   const halfWidth = Math.round(availableWidth / 2);

//   const { indexes } = getViewportStart(
//     sheet.cols,
//     sheet.hiddenCols,
//     halfWidth,
//     ci,
//     frozenEnd,
//     sheet.maxColIndex,
//     sheet.defaultColWidth,
//     sheet.zoom,
//     sheet.freezedCols
//   );

//   const { start } = getViewportStartEnd(indexes);
//   return getViewportCols(
//     windowWidth,
//     sheet,
//     sheetViewportView,
//     Direction.Right,
//     start
//   );
// };
